/**
 * The Rename / Move dialog's requests (S8 Task 8.10): submit (the FIRST request always carries the checked source ETag),
 * Continue (a `ready` token, or a replay-safe uncertain continuation — main decides and re-checks), Check again (the
 * read-only re-probe; what it settled comes from main's evidence, `settledMoveOf`). Every request goes through ONE latch (`useHnsPathMutation`), so a double submission can never
 * dispatch twice; every completed action fires `feature:storage_hns_action` once.
 */
import { useCallback, useRef, useState } from 'react';
import type { HnsLocator, HnsMoveResult, HnsPathRef } from '../../../../../types/azureStorage';
import { hnsMove, hnsPendingMoves } from '../hnsApi';
import { outcomeOfHnsError, trackHnsAction, type HnsActionName } from '../hnsActionAnalytics';
import { settledMoveOf, settledMoveOutcome, settledMoveText } from '../pendingMoveSettle';
import { useHnsPathMutation, type HnsMutationOutcome } from '../useHnsPathMutation';
import type { HnsActionSource } from './hnsDialogTypes';
import type { MoveStatus } from './MoveStatusNotice';

type Args = { connectionId: string; resource: HnsPathRef; action: 'rename' | 'move'; origin: HnsActionSource; tabId?: string; onFinished: (resource: HnsPathRef) => void };

export function useMovePathActions({ connectionId, resource, action, origin, tabId, onFinished }: Args) {
  const { run, pending, cancel } = useHnsPathMutation();
  const [status, setStatus] = useState<MoveStatus>({ kind: 'idle' });
  const destination = useRef<HnsLocator | null>(null);

  const learnCanContinue = useCallback((pendingMoveId: string) => {
    void hnsPendingMoves({ connectionId, action: 'list' }, { tabId }).then((answer) => {
      const move = answer.ok === true ? answer.value.moves.find((candidate) => candidate.id === pendingMoveId) : undefined;
      if (move?.canContinue) setStatus((current) => (current.kind === 'uncertain' && current.pendingMoveId === pendingMoveId ? { ...current, canContinue: true } : current));
    });
  }, [connectionId, tabId]);

  const apply = useCallback((result: HnsMutationOutcome<HnsMoveResult>, name: HnsActionName) => {
    if (!result.started) return;
    if (!result.current) { trackHnsAction(name, 'cancelled', origin); return; }
    const target = destination.current!;
    if (result.error) {
      trackHnsAction(name, outcomeOfHnsError(result.error), origin);
      setStatus(result.error.category === 'ambiguous_outcome' ? { kind: 'ambiguous' } : { kind: 'error', error: result.error });
      return;
    }
    const value = result.value!;
    if (value.state === 'moved') { trackHnsAction(name, 'ok', origin); onFinished(value.resource); return; }
    trackHnsAction(name, value.state, origin);
    if (value.state === 'partial') { setStatus({ kind: 'partial', pendingMoveId: value.pendingMoveId, destination: target }); return; }
    setStatus({ kind: 'uncertain', pendingMoveId: value.pendingMoveId, destination: target, canContinue: false });
    learnCanContinue(value.pendingMoveId);
  }, [learnCanContinue, onFinished, origin]);

  const submit = useCallback(async (sourceRef: HnsPathRef, sourceEtag: string, target: HnsLocator) => {
    destination.current = target;
    const result = await run((requestId) => hnsMove({ connectionId, source: sourceRef, sourceEtag, destination: target, requestId }, { tabId }));
    apply(result, action);
  }, [action, apply, connectionId, run, tabId]);

  const continueMove = useCallback(async () => {
    if (status.kind !== 'partial' && status.kind !== 'uncertain') return;
    const result = await run((requestId) => hnsMove({ connectionId, continuePendingMoveId: status.pendingMoveId, requestId }, { tabId }));
    apply(result, 'continue_move');
  }, [apply, connectionId, run, status, tabId]);

  const recheck = useCallback(async () => {
    if (status.kind !== 'uncertain') return;
    const result = await run((requestId) => hnsPendingMoves({ connectionId, action: 'recheck', id: status.pendingMoveId, requestId }, { tabId }));
    if (!result.started || !result.current) return;
    if (result.error) { trackHnsAction('recheck_pending_move', outcomeOfHnsError(result.error), origin); setStatus({ kind: 'error', error: result.error }); return; }
    const settled = settledMoveOf(result.value!, status.pendingMoveId);
    trackHnsAction('recheck_pending_move', settledMoveOutcome(settled), origin);
    if (settled.state === 'pending') {
      const still = settled.move;
      setStatus(still.state === 'ready' ? { kind: 'partial', pendingMoveId: still.id, destination: status.destination } : { ...status, canContinue: still.canContinue });
      return;
    }
    if (settled.state === 'moved') { onFinished(settled.resource); return; }
    setStatus({ kind: 'error', error: { category: settled.state === 'not-moved' ? 'not_found' : 'unknown', code: 'MoveSettled', retryable: false, message: settledMoveText(settled, resource) } });
  }, [connectionId, run, onFinished, origin, resource, status, tabId]);

  const resetStatus = useCallback(() => setStatus({ kind: 'idle' }), []);
  return { status, pending, submit, continueMove, recheck, resetStatus, cancel };
}
